import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import AssistantAlertCarousel from '../../components/AssistantAlertCarousel';
import BillSlideout from '../../components/slideouts/BillSlideout';
import RecurringTransactionSlideout from '../../components/slideouts/RecurringTransactionSlideout';
import TransactionSlideout from '../../components/slideouts/TransactionSlideout';
import { useDrainPendingAlertExpand } from '../../hooks/assistant/useDrainPendingAlertExpand';
import { useAssistantWebSocket } from '../../hooks/useAssistantWebSocket';
import { collectAlertCardIds } from '../../lib/assistant/alertCards';
import { useAssistantTurnStatus } from '../../providers/AssistantTurnStatusProvider';
import { useAuthState } from '../../providers/AuthProvider';
import { entityDestination } from '../../utils/entityDestination';

import AssistantClientToolRunner from './AssistantClientToolRunner';
import AssistantPaneView from './AssistantPaneView';
import { useAlertCardWorkItems } from './useAlertCardWorkItems';
import { useTranscriptEntities } from './useTranscriptEntities';

import type { EntityTarget } from '../../utils/entityDestination';

/** Module-level so the initial set is referentially stable across renders. */
const EMPTY_ALERT_IDS: ReadonlySet<string> = new Set();

/**
 * Top-level "Mantle Assistant" pane (route `/assistant`). Streams a
 * Claude-backed financial chat over WebSocket. Visibility is gated at the
 * route boundary by `feat-assistant` (`AssistantRouteGate` in `MainApp.tsx`);
 * this pane itself does not re-check that gate.
 *
 * Thin wrapper around {@link AssistantPaneView} — owns the hook, hands its
 * return shape to the presentational view. The split exists so Storybook
 * stories and visual-verification subagents can render the view from fixture
 * data without mocking the hook. The alert strip is mounted here and passed down
 * as `alertsSlot` for the same reason: it fetches, and the view must not.
 *
 * Tool-call detail is not rendered. When the `debug-assistant` gate is on, the
 * server streams each tool's name / args / output on the live frames and the
 * hook logs them to the browser console as they complete.
 */
interface IAssistantPaneProps {
  /** Renders the header's close button — supplied by the `xl+` side panel only. */
  onClose?: () => void;
}

const AssistantPane = ({ onClose }: IAssistantPaneProps) => {
  const { session } = useAuthState();

  // Chat auth is cookie-based — pass a boolean instead of any credential.
  // The browser attaches `_s` to the WS upgrade automatically.
  const {
    messages,
    isProcessing,
    isAbortable,
    isRehydrating,
    threadGeneration,
    connectionStatus,
    error,
    sendMessage,
    expandAlert,
    stopTurn,
    reclaim,
    pendingClientTools,
    sendClientToolResult,
  } = useAssistantWebSocket(session != null);

  // Publishes the turn state for the alert surfaces — the Alerts Center is
  // this pane's sibling (or a separate route), so it cannot read `isProcessing`
  // directly. Layout-synchronous so the flag is readable before the paint of
  // the render that flipped `isProcessing`; a passive effect would leave a
  // painted frame where a click still passes the mid-turn gate. The cleanup
  // keeps the flag honest across unmount: no pane means no socket, and no
  // socket means no client-side turn.
  const { setTurnInFlight } = useAssistantTurnStatus();
  React.useLayoutEffect(() => {
    setTurnInFlight(isProcessing);
    return () => setTurnInFlight(false);
  }, [isProcessing, setTurnInFlight]);

  // Alerts opened while still unread. The recap treatment asks whether the
  // alert was new to the user, and the click that opens one also marks it read
  // — so by the time the reply renders, the stored status has already moved and
  // can no longer answer that. Every opener reports what the row said when it
  // was clicked, and this remembers it.
  const [freshlyOpenedAlertIds, setFreshlyOpenedAlertIds] = React.useState<ReadonlySet<string>>(EMPTY_ALERT_IDS);

  // The memory is per thread, not per tab. `/reset` replaces the transcript
  // without remounting this pane, so without this the exclusion would outlive
  // the conversation that justified it and a genuinely past alert would reopen
  // in the new thread reading as news.
  React.useEffect(() => {
    setFreshlyOpenedAlertIds(EMPTY_ALERT_IDS);
  }, [threadGeneration]);

  const openAlert = React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      if (wasUnread) {
        setFreshlyOpenedAlertIds((previous) => new Set(previous).add(alertId));
      }
      expandAlert(alertId);
    },
    [expandAlert]
  );

  // An Alerts Center row click cannot reach `expandAlert` — that surface is
  // this pane's sibling at `xl+` and a different route below it — so it leaves
  // the alert on the panel context and this drains it once the pane is up.
  useDrainPendingAlertExpand(openAlert);

  // Hoisted for the same reason the alert strip is: the view fetches nothing.
  const { cards: alertCards, workedAlertIds } = useAlertCardWorkItems(messages);
  // Which alerts the transcript actually holds a card for — the strip's own
  // "already open" test. Derived here rather than kept as strip state because a
  // click is optimistic and an expand can fail; see `AssistantAlertCarousel`.
  const expandedAlertIds = React.useMemo(() => collectAlertCardIds(messages), [messages]);
  const entities = useTranscriptEntities(messages);

  // Run client tools one at a time — the head of the queue — so two browser
  // flows (e.g. two Plaid modals) never overlap. When the head reports its
  // result it leaves the queue and the next invocation takes over.
  const activeClientTool = pendingClientTools[0];

  const navigate = useNavigate();
  const [slideoutTransactionId, setSlideoutTransactionId] = React.useState<string | null>(null);
  const [slideoutBillId, setSlideoutBillId] = React.useState<string | null>(null);
  const [slideoutRecurringTransactionId, setSlideoutRecurringTransactionId] = React.useState<string | null>(null);

  // Opening a chip's row belongs to the pane, not the view: a transaction, bill,
  // or recurring stream has no route, so the only way to show one is to mount
  // its slideout and hold the id — the pattern every table that opens a
  // transaction already uses.
  //
  // At `xl+` a navigate swaps the content column beside this pane and the
  // conversation stays open; below `xl` the assistant *is* the route, so the
  // same click leaves it. That is deliberate and matches an alert row's click.
  const handleEntitySelect = React.useCallback(
    (target: EntityTarget) => {
      const destination = entityDestination(target);
      switch (destination.kind) {
        case 'navigate':
          void navigate(destination.path);
          break;
        case 'transactionSlideout':
          setSlideoutTransactionId(destination.transactionId);
          break;
        case 'billSlideout':
          setSlideoutBillId(destination.billId);
          break;
        case 'recurringTransactionSlideout':
          setSlideoutRecurringTransactionId(destination.recurringTransactionId);
          break;
        default: {
          const _exhaustive: never = destination;
          return _exhaustive;
        }
      }
    },
    [navigate]
  );

  return (
    <>
      <AssistantPaneView
        messages={messages}
        isProcessing={isProcessing}
        isAbortable={isAbortable}
        isRehydrating={isRehydrating}
        // A pending client tool means the turn is parked on the browser (the user
        // is finishing the Plaid flow) — suppress the "Thinking…" spinner.
        isAwaitingClientTool={activeClientTool !== undefined}
        connectionStatus={connectionStatus}
        error={error}
        onSend={sendMessage}
        onStop={stopTurn}
        onReclaim={reclaim}
        onClose={onClose}
        alertCards={alertCards}
        workedAlertIds={workedAlertIds}
        viewerUserId={session?.user.id ?? null}
        freshlyOpenedAlertIds={freshlyOpenedAlertIds}
        entities={entities}
        onSuggestionSelect={sendMessage}
        onEntitySelect={handleEntitySelect}
        alertsSlot={<AssistantAlertCarousel onExpandAlert={openAlert} expandedAlertIds={expandedAlertIds} />}
      />
      <TransactionSlideout
        transactionId={slideoutTransactionId}
        isOpen={slideoutTransactionId !== null}
        onClose={() => setSlideoutTransactionId(null)}
      />
      <BillSlideout billId={slideoutBillId} isOpen={slideoutBillId !== null} onClose={() => setSlideoutBillId(null)} />
      <RecurringTransactionSlideout
        recurringTransactionId={slideoutRecurringTransactionId}
        isOpen={slideoutRecurringTransactionId !== null}
        onClose={() => setSlideoutRecurringTransactionId(null)}
      />
      {/* Runs a browser-side client tool (e.g. Plaid Link) when the model calls
          one, then reports the result. Keyed on the invocation so the next
          queued tool remounts and a server cancel unmounts + tears down. */}
      {activeClientTool && (
        <AssistantClientToolRunner
          key={activeClientTool.toolId}
          invocation={activeClientTool}
          onResult={sendClientToolResult}
        />
      )}
    </>
  );
};

export default AssistantPane;
